import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDemo } from '../context/DemoContext';

interface UnifiedChatViewProps {
  isMobileSidebarOpen?: boolean;
  setIsMobileSidebarOpen?: (open: boolean) => void;
}

const UnifiedChatView: React.FC<UnifiedChatViewProps> = ({ 
  isMobileSidebarOpen = false, 
  setIsMobileSidebarOpen 
}) => {
  const { state, startDemo } = useDemo();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const MIN_SIDEBAR_WIDTH = 250;
  const MAX_SIDEBAR_WIDTH = 500;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, e.clientX));
    setSidebarWidth(newWidth);
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen?.(false);
  }, [setIsMobileSidebarOpen]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Get participants with status
  const getParticipantStatus = (participantName: string) => {
    if (participantName.includes('Customer') || participantName.includes('Mike')) {
      return 'online'; // Always online for customer and human
    }
    
    // For virtual agents, check their current status
    const agentName = participantName.split(' (')[0].toLowerCase();
    const agent = state.agents.find(a => a.name.toLowerCase().includes(agentName));
    
    if (!agent) return 'offline';
    
    switch (agent.status) {
      case 'thinking':
        return 'thinking';
      case 'acting':
      case 'communicating':
        return 'acting';
      case 'idle':
      default:
        return 'online';
    }
  };

  const humanCustomers = [
    { name: "Sarah Wilson (Customer)", type: "customer", avatar: "SW", color: "#3b82f6", isHuman: true, status: getParticipantStatus("Sarah Wilson") }
  ];

  const humanEmployees = [
    { name: "Emma Johnson (Warehouse)", type: "human", avatar: "EJ", color: "#059669", isHuman: true, status: getParticipantStatus("Emma Johnson") },
    { name: "Winston Smith (Operations Director)", type: "human", avatar: "WS", color: "#4f46e5", isHuman: true, status: getParticipantStatus("Winston Smith") }
  ];

  const virtualEmployees = [
    { name: "Julia (Virtual Support)", type: "agent", avatar: "🤖", color: "#0891b2", isHuman: false, status: getParticipantStatus("Julia") },
    { name: "Oliver (Virtual Operations)", type: "agent", avatar: "🤖", color: "#16a34a", isHuman: false, status: getParticipantStatus("Oliver") },
    { name: "Alex (Virtual Manager)", type: "agent", avatar: "🤖", color: "#7c3aed", isHuman: false, status: getParticipantStatus("Alex") }
  ];

  const scrollToBottom = () => {
    // Add delay for mobile to ensure content is rendered
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [state.messages, state.interAgentMessages, state.typingUsers]);

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  };

  const formatMentions = (text: string) => {
    return text.replace(/@([A-Za-z]+)/g, '<span class="mention">@$1</span>');
  };

  const formatLinks = (text: string) => {
    // Make emails clickable BEFORE processing bold text to avoid conflicts
    text = text.replace(/Email:\s*([\w\.-]+@[\w\.-]+\.\w+)/g, 'Email: <a href="mailto:$1">$1</a>');
    text = text.replace(/Contact:\s*([\w\.-]+@[\w\.-]+\.\w+)/g, 'Contact: <a href="mailto:$1">$1</a>');
    text = text.replace(/\*\*Contact:\*\*\s*([\w\.-]+@[\w\.-]+\.\w+)/g, '<strong>Contact:</strong> <a href="mailto:$1">$1</a>');
    // Make bold text (after email processing)
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Make websites clickable (only after Website: label)
    text = text.replace(/Website:\s*((?:https?:\/\/)?(?:www\.)?[\w\.-]+\.\w+(?:\/[\w\.-]*)*)/g, (match, url) => {
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      return `Website: <a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    return text;
  };

  const getBotRole = (senderName: string) => {
    const roleMap: { [key: string]: string } = {
      'Julia': '(Virtual Support)',
      'Oliver': '(Virtual Operations)',
      'Alex': '(Virtual Manager)'
    };
    return roleMap[senderName] || '';
  };

  const getAgentIcon = (status: string) => {
    switch (status) {
      case 'thinking':
        return 'thinking';
      case 'acting':
        return 'acting';
      case 'communicating':
        return 'communicating';
      default:
        return 'idle';
    }
  };

  // Combine and sort all messages by timestamp
  const allMessages = [
    ...state.messages.map(m => ({ ...m, type: 'customer' as const })),
    ...state.interAgentMessages.map(m => ({ ...m, type: 'internal' as const }))
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  
  // Debug log for inter-agent messages
  console.log('DEBUG: All interAgentMessages:', state.interAgentMessages);
  console.log('DEBUG: Conclusion messages found:', state.interAgentMessages.filter(m => m.messageType === 'conclusion'));

  const hasStarted = state.messages.length > 0 || state.interAgentMessages.length > 0;

  return (
    <div className="chat-interface-container">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`mobile-sidebar-overlay ${isMobileSidebarOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      />
      
      {/* Left Sidebar */}
      <div className={`chat-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`} style={{ width: `${sidebarWidth}px` }}>
        {/* Participants */}
        <div className="participants-section">
          {/* Human Customers */}
          <div className="participants-group">
            <h4 className="group-title-simple">Human Customers</h4>
            <div className="participants-list">
              {humanCustomers.map((participant, index) => (
                <div key={`customer-${index}`} className="participant-item">
                  <div className="participant-avatar-container">
                    <div 
                      className="participant-avatar human-avatar"
                      style={{backgroundColor: participant.color}}
                    >
                      {participant.avatar}
                    </div>
                  </div>
                  <span className="participant-name">{participant.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Human Employees */}
          <div className="participants-group">
            <h4 className="group-title-simple">Human Employees</h4>
            <div className="participants-list">
              {humanEmployees.map((participant, index) => (
                <div key={`human-${index}`} className="participant-item">
                  <div className="participant-avatar-container">
                    <div 
                      className="participant-avatar human-avatar"
                      style={{backgroundColor: participant.color}}
                    >
                      {participant.avatar}
                    </div>
                  </div>
                  <span className="participant-name">{participant.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Virtual Employees */}
          <div className="participants-group">
            <h4 className="group-title-simple">Virtual Employees</h4>
            <div className="participants-list">
              {virtualEmployees.map((participant, index) => (
                <div key={`virtual-${index}`} className="participant-item">
                  <div className="participant-avatar-container">
                    <div 
                      className="participant-avatar robot-avatar"
                      style={{backgroundColor: participant.color}}
                    >
                      {participant.avatar}
                    </div>
                  </div>
                  <span className="participant-name">{participant.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        
        {/* Resize Handle */}
        <div 
          className="sidebar-resize-handle"
          onMouseDown={handleMouseDown}
        />
      </div>

      {/* Main Chat Area */}
      <div className="chat-interface" style={{ left: `${sidebarWidth}px` }}>

      {/* Messages Area */}
      <div className="chat-messages">
        {!hasStarted && (
          <div className="demo-intro">
            <h3><strong>🚀 Welcome to the Future of Work</strong></h3>
            <div className="demo-explanation">
              <p>In this demo, you'll meet Virtual Employees powered by <strong>Aisberg Artificial Mind</strong>.<br/>
              Not chatbots — but digital colleagues who feel, think, act proactively, and learn continuously.</p>
              
              <div className="demo-scenario-box">
                <p><strong>📦 Demo Scenario</strong><br/>
                At ABC Global Retail, a VIP customer's delivery went missing.<br/>
                Watch how Virtual and Human employees collaborate to turn a crisis into improvement.</p>
              </div>
              
              <div className="personality-showcase">
                <p><strong>🤖 Julia</strong> — empathetic and calming<br/>
                <strong>🤖 Oliver</strong> — analytical and precise<br/>
                <strong>🤖 Alex</strong> — strategic and decisive</p>
                
                <p>👥 Working seamlessly with their human colleagues: Emma (Warehouse) and Winston (Director).</p>
                
                <p><strong>👉 <span className="start-demo-link" onClick={startDemo}>Start Demo</span> to see digital colleagues in action</strong></p>
              </div>
            </div>
          </div>
        )}
        
        {allMessages.map((message, index) => {
          // Debug log for all messages
          if (message.type === 'internal' && (message as any).messageType === 'conclusion') {
            console.log('DEBUG: Found conclusion message in React:', message);
          }
          
          if (message.type === 'customer') {
            const customerMessage = message as typeof state.messages[0] & { type: 'customer' };
            return (
              <div
                key={`customer-${customerMessage.id}`}
                className={`flex ${customerMessage.senderType === 'customer' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`message ${
                    customerMessage.senderType === 'customer'
                      ? 'message-customer'
                      : 'message-agent'
                  }`}
                >
                  <div className="message-header">
                    {customerMessage.senderType === 'customer' ? (
                      <span className="sender-name">👤 Sarah Wilson (VIP Customer)</span>
                    ) : (
                      <span className="sender-name">🤖 {customerMessage.sender} {getBotRole(customerMessage.sender)}</span>
                    )}
                    <span className="timestamp-inline">
                      {formatTime(customerMessage.timestamp)}
                    </span>
                  </div>
                  <p>{customerMessage.content}</p>
                </div>
              </div>
            );
          } else {
            const internalMessage = message as typeof state.interAgentMessages[0] & { type: 'internal' };
            
            // Handle activity-group with new elegant format
            if (internalMessage.messageType === 'activity-group') {
              return (
                <div key={`internal-${internalMessage.id}`} className="agent-activity-group">
                  <pre className="agent-activity-content">{internalMessage.content}</pre>
                </div>
              );
            }

            // Handle conclusion with expandable accordion
            if (internalMessage.messageType === 'conclusion') {
              console.log('DEBUG: Rendering conclusion accordion for message:', internalMessage);
              const conclusionId = `conclusion-${internalMessage.id}`;
              
              const toggleConclusion = () => {
                const contentDiv = document.getElementById(`${conclusionId}-content`);
                const arrow = document.getElementById(`${conclusionId}-arrow`);
                
                if (contentDiv && arrow) {
                  if (contentDiv.style.display === 'none' || contentDiv.style.display === '') {
                    contentDiv.style.display = 'block';
                    arrow.classList.add('expanded');
                  } else {
                    contentDiv.style.display = 'none';
                    arrow.classList.remove('expanded');
                  }
                }
              };
              
              return (
                <div key={`internal-${internalMessage.id}`} className="demo-conclusion">
                  <div className="conclusion-toggle" onClick={toggleConclusion}>
                    <span className="conclusion-toggle-text">
                      📈 See Demo Conclusion
                    </span>
                    <span 
                      id={`${conclusionId}-arrow`}
                      className="conclusion-toggle-arrow"
                    >
                      ▼
                    </span>
                  </div>
                  <div 
                    id={`${conclusionId}-content`}
                    className="conclusion-content-wrapper"
                    style={{ display: 'none' }}
                  >
                    <pre className="conclusion-content" dangerouslySetInnerHTML={{ 
                      __html: formatLinks(internalMessage.content) 
                    }}></pre>
                  </div>
                </div>
              );
            }
            
            // Check if this is a console message (no framing needed)
            const isConsoleMessage = internalMessage.messageType === 'decision' || 
                              internalMessage.messageType === 'alert' ||
                              internalMessage.messageType === 'emotion' ||
                              internalMessage.messageType === 'thought' ||
                              internalMessage.messageType === 'action' ||
                              internalMessage.content.includes('APPROVED:') ||
                              internalMessage.content.includes('URGENT:');
            
            if (isConsoleMessage) {
              // For emotion/thought/action, show only content without prefix
              const showOnlyContent = internalMessage.messageType === 'emotion' || 
                                     internalMessage.messageType === 'thought' || 
                                     internalMessage.messageType === 'action';
              
              return (
                <div key={`internal-${internalMessage.id}`} className="console-message">
                  {showOnlyContent ? (
                    <span>{internalMessage.content}</span>
                  ) : (
                    <span><strong>{internalMessage.from.charAt(0).toUpperCase() + internalMessage.from.slice(1)} → {internalMessage.to.charAt(0).toUpperCase() + internalMessage.to.slice(1)}:</strong> {internalMessage.content}</span>
                  )}
                </div>
              );
            }
            
            // Get sender info for styling with unique colors
            const getSenderInfo = (senderName: string) => {
              const colorMap = {
                'emma': '#059669',
                'winston': '#4f46e5', 
                'alex': '#7c3aed',
                'julia': '#0891b2',
                'oliver': '#16a34a'
              };
              
              const name = senderName.toLowerCase();
              const color = colorMap[name as keyof typeof colorMap] || '#666';
              
              // Get proper display name with emoji
              const displayNames = {
                'emma': '👤 Emma Johnson',
                'winston': '👤 Winston Smith',
                'alex': '🤖 Alex',
                'julia': '🤖 Julia', 
                'oliver': '🤖 Oliver'
              };
              
              return { 
                name: displayNames[name as keyof typeof displayNames] || senderName.charAt(0).toUpperCase() + senderName.slice(1), 
                color 
              };
            };

            const senderInfo = getSenderInfo(internalMessage.from);
            
            // Clean content by removing "Internal message to" prefix
            const cleanContent = internalMessage.content.replace(/^Internal message to /, '');

            // Regular internal communication (conversational)
            return (
              <div key={`internal-${internalMessage.id}`} className="internal-message-container">
                <div className="message message-agent message-internal">
                  <div className="message-header">
                    <div className="sender-with-label">
                      <span className="sender-name" style={{ color: senderInfo.color }}>
                        {senderInfo.name}
                      </span>
                      <span className="internal-message-label-inline">[INTERNAL MESSAGE]</span>
                    </div>
                    <span className="timestamp-inline">
                      {formatTime(internalMessage.timestamp)}
                    </span>
                  </div>
                  <p dangerouslySetInnerHTML={{ __html: formatMentions(cleanContent) }}></p>
                </div>
              </div>
            );
          }
        })}
        
        {/* Show typing indicators - only if demo has started */}
        {hasStarted && Object.entries(state.typingUsers).map(([sender, isTyping]) => 
          isTyping && (
            <div key={`typing-${sender}`} className="flex justify-start">
              <div className="typing-indicator">
                <span>{sender}</span>
                <div className="typing-dots">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            </div>
          )
        )}
        
        
        <div ref={messagesEndRef} />
      </div>

    </div>
    </div>
  );
};

export default UnifiedChatView;